import React, { useState } from 'react';

interface NavSidebarProps {
  activeView: string;
  onNavigate: (view: string) => void;
  attentionManaged?: boolean;
}

interface NavItem { id: string; label: string; icon: string; }

// #285 B7: grouped into workflow sections (Create → Measure → Distribute)
// instead of a flat 12-item emoji rail. Every item carries an aria-label so
// screen readers announce the destination, not just the emoji.
const NAV_SECTIONS: { title: string; items: NavItem[] }[] = [
  { title: 'Create', items: [
    { id: 'library', label: 'Library', icon: '\u{1F4C4}' },
    { id: 'ebooks', label: 'Ebooks', icon: '\u{1F4DA}' },
    { id: 'newsletter', label: 'Newsletter', icon: '\u{1F4EC}' },
    { id: 'social-studio', label: 'Social Studio', icon: '✍️' },
  ] },
  { title: 'Operate', items: [
    { id: 'attention', label: 'Attention', icon: '\u25CE' },
  ] },
  { title: 'Measure', items: [
    { id: 'quality', label: 'Quality', icon: '\u{1F4CA}' },
    { id: 'brand-performance', label: 'Brand Performance', icon: '\u{1F4C8}' },
    { id: 'cost', label: 'Spend', icon: '\u{1F4B0}' },
  ] },
  { title: 'Distribute', items: [
    { id: 'channels', label: 'Channels', icon: '\u{1F4E1}' },
    { id: 'linkedin', label: 'LinkedIn', icon: '\u{1F4AC}' },
    { id: 'outreach', label: 'Outreach', icon: '\u{1F4E7}' },
    // Distinct from LinkedIn's 💬 (they used to share an icon).
    { id: 'comments-approval', label: 'Comments', icon: '\u{1F5E8}️' },
  ] },
];

const BOTTOM_ITEMS: NavItem[] = [
  { id: 'settings', label: 'Settings', icon: '⚙️' },
  { id: 'activity', label: 'Activity', icon: '\u{1F504}' },
];

export function visibleNavSections(attentionManaged: boolean): typeof NAV_SECTIONS {
  const legacyAttentionDestinations = new Set(['linkedin', 'comments-approval']);
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !(attentionManaged && legacyAttentionDestinations.has(item.id))),
  }));
}

export default function NavSidebar({ activeView, onNavigate, attentionManaged = false }: NavSidebarProps) {
  const [expanded, setExpanded] = useState(false);

  const renderItem = (item: NavItem) => {
    const active = activeView === item.id;
    return (
      <button
        key={item.id}
        className={`nav-sidebar__item ${active ? 'nav-sidebar__item--active' : ''}`}
        onClick={() => onNavigate(item.id)}
        title={item.label}
        aria-label={item.label}
        aria-current={active ? 'page' : undefined}
      >
        <span className="nav-sidebar__icon" aria-hidden="true">{item.icon}</span>
        {expanded && <span className="nav-sidebar__label">{item.label}</span>}
      </button>
    );
  };

  return (
    <nav
      className={`nav-sidebar ${expanded ? 'nav-sidebar--expanded' : ''}`}
      aria-label="Primary"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      <div className="nav-sidebar__top">
        {visibleNavSections(attentionManaged).map((section, i) => (
          <React.Fragment key={section.title}>
            {i > 0 && <div className="sidebar__divider" role="separator" />}
            {expanded && <div className="sidebar__section-title" role="heading" aria-level={3}>{section.title}</div>}
            {section.items.map(renderItem)}
          </React.Fragment>
        ))}
      </div>
      <div className="nav-sidebar__bottom">
        {BOTTOM_ITEMS.map(renderItem)}
      </div>
    </nav>
  );
}
